import { GAME_ID, REDUX_ACTIONS, LEVEL_ITEM_CONFIGURATIONS, SCREENS } from '../../../data/constants';
import { getRandomInt, shuffleArray } from '../../../../../lib/utilityFunctions';
import { generateListOfItems } from '../../../lib/utilityFunctions';
import { getCurriculum } from '../../../content/curriculum/curriculumStorage';

const initialState = {
  level: 0,
  listOfItemsForLevel: [], // the items that will be played in the current round.
  standardIndex: 0, // our current position in the itemsForLevel array.
  currentItemUuid: '', // the uuid of the item currently in use (for embedded measurement).
  currentItemId: '', // the item id of the item currently in use.
  currentImage: '', // the name of the resource for the current image on single image items.
  currentPanels: [], // the list of resource names for multi image items.
  currentText: '', // the passage for text item.
  currentChoices: [], // the shuffled list of choices available in the current item.
  correctIndex: 0, // the index in the shuffled list where the correct choice resides.
  isItemCompletionAnimationBeingBlocked: false, // wether or not onboarding is preventing the game from moving to the next item
  masteredItemIds: [], // items the user has already answered correctly on their first try.
  seenItemIds: [], // items the user has already seen at some point.
  contentSideIdByLevel: [], // is level content A or B
};

const curriculumReducer = (state = initialState, { type, payload, gameId }) => {
  if (gameId !== GAME_ID) {
    return state;
  }

  switch (type) {
    case REDUX_ACTIONS.SET_LEVEL: {
      const { level, isIncludingOnboarding } = payload;
      const { seenItemIds, masteredItemIds, contentSideIdByLevel } = state;
      // always default to side A content
      if (contentSideIdByLevel[level] === undefined) {
        contentSideIdByLevel[level] = 'sideA';
      } else if (contentSideIdByLevel[level] === 'sideA') {
        contentSideIdByLevel[level] = 'sideB';
      } else {
        contentSideIdByLevel[level] = 'sideA';
      }
      let completeSet = [...getCurriculum().levels[level]];
      if (contentSideIdByLevel[level] === 'sideB') {
        completeSet = completeSet.map(elem => elem.sideB || elem);
      }
      // figure out how many items are reserved for onboarding in this level.
      // variety onboarding reserves the first three items for onboarding b/c it needs to be able to show a single image, multi image, and text item.
      const onboardingItemCount = LEVEL_ITEM_CONFIGURATIONS[level].screen === SCREENS.VARIETY ? 3 : 1;
      // take items out of the beginning of the array that are reserved for onboarding.
      const onboardingItems = [];
      for (let i = 0; i < onboardingItemCount; i++) {
        onboardingItems.push(completeSet.shift());
      }

      // generate a list of items for the round using items reserved for standard play.
      const { listOfItems, usedUnseenItems } = generateListOfItems({
        completeSet,
        seenItemIds: seenItemIds[level] || [],
        masteredItemIds,
        desiredLength: LEVEL_ITEM_CONFIGURATIONS[level].itemsInRound,
      });

      const newSeenItemIds = [...seenItemIds];
      if (!usedUnseenItems) {
        // if we've seen every item once already, we can reset and consider them all 'unseen' again.
        newSeenItemIds[level] = [];
      }

      // add back the reserved onboarding items if we ask for them.
      if (isIncludingOnboarding) {
        for (let i = 0; i < onboardingItemCount; i++) {
          listOfItems.unshift(onboardingItems.pop());
        }
      }

      return {
        ...initialState,
        level,
        standardIndex: 0,
        listOfItemsForLevel: listOfItems,
        seenItemIds: newSeenItemIds,
      };
    }

    case REDUX_ACTIONS.NEXT_STANDARD_ITEM: {
      let standardIndex = payload?.nextItemIndex ?? state.standardIndex + 1;
      if (standardIndex >= state.listOfItemsForLevel.length) {
        standardIndex = 0;
      }
      const seenItemIds = [...state.seenItemIds];
      if (seenItemIds[state.level] === undefined) {
        seenItemIds[state.level] = [];
      }
      seenItemIds[state.level].push(state.currentItemId);

      return {
        ...state,
        standardIndex,
        seenItemIds,
      };
    }

    case REDUX_ACTIONS.INIT_STANDARD_ITEM: {
      const currentItem = state.listOfItemsForLevel[state.standardIndex];

      let currentPanels = [];
      let currentText = '';
      let currentImage = '';
      if (currentItem.image !== undefined) {
        currentImage = currentItem.image;
      }
      if (currentItem.panels !== undefined) {
        currentPanels = [...currentItem.panels];
      }
      if (currentItem.text !== undefined) {
        currentText = currentItem.text;
      }

      const currentChoices = [];
      const shuffledDistractors = shuffleArray([...currentItem.possibleDistractors]);
      const numCarouselItems = shuffledDistractors.length + 1;
      const correctIndex = getRandomInt(0, numCarouselItems);
      let distractorIndex = 0;
      for (let i = 0; i < numCarouselItems; i++) {
        if (i === correctIndex) {
          currentChoices.push(currentItem.correctChoice);
        } else {
          currentChoices.push(shuffledDistractors[distractorIndex]);
          distractorIndex++;
        }
      }
      return {
        ...state,
        currentChoices,
        currentImage,
        currentPanels,
        currentText,
        correctIndex,
        currentItemUuid: currentItem.uuid,
        currentItemId: currentItem.id,
      };
    }

    case REDUX_ACTIONS.SET_BLOCKING_ITEM_COMPLETION_ANIMATION:
      return {
        ...state,
        isItemCompletionAnimationBeingBlocked: payload,
      };

    case REDUX_ACTIONS.BANK_MASTERED_ITEMS:
      return {
        ...state,
        masteredItemIds: [...state.masteredItemIds, ...payload],
      };

    case REDUX_ACTIONS.BANK_SEEN_ITEMS:
      return {
        ...state,
        seenItemIds: payload,
      };

    case REDUX_ACTIONS.CHANGE_SCREEN:
      if (payload === SCREENS.PROGRESS) {
        return {
          ...initialState,
          seenItemIds: state.seenItemIds,
        };
      }
      return {
        ...state,
        isItemCompletionAnimationBeingBlocked: false,
      };

    default:
      return state;
  }
};

export default curriculumReducer;
